// ============================================================
// defence/claim - claiming a shared defence prompt on the server.
//
// Whoever claims the pending-defence document first resolves it; a
// second resolver would apply the hits twice.
// ============================================================
import { diag } from '../../diagnostics/log';
import type { MultiplayerService } from './types';

/**
 * How long a claim keeps asking for a prompt the server has not got yet.
 *
 * The attacker's create is a write in flight; the defender's view showed
 * the prompt from the local copy the moment it was written. On a loaded
 * server the acknowledgement can trail the click by seconds, and a claim
 * that read the gap as "gone" gave up for good: the hits were never
 * applied, nothing cleared the prompt, and every later choice answered
 * the same way (v8.310.17). Three re-asks over ~2.5 s outlast the gap;
 * `taken` is never re-asked, since a second resolver would double the hits.
 */
const CLAIM_RETRY_MS = [300, 700, 1500];

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** One claim: who asks, for which prompt, with which choice. */
export interface DefenceClaim {
  sessionId: string;
  tabId: string;
  playerId: string;
  choice: string;
  extra?: Record<string, unknown>;
}

/** The pre-claim service: one transaction, won or lost. */
async function resolveOnce(mp: MultiplayerService, c: DefenceClaim): Promise<unknown> {
  const result = await mp.resolvePendingDefence(c.sessionId, c.tabId, c.playerId, c.choice);
  diag.info('defence', `claim of ${c.tabId} ${result ? 'won' : 'lost'}`);
  return result;
}

/** Give up for good: somebody resolved it first, or it never arrived. */
function claimLost(tabId: string, reason: string): null {
  const why = reason === 'taken' ? 'somebody resolved it first' : 'still missing after re-asking';
  diag.warn('defence', `claim of ${tabId} lost - ${why}`);
  return null;
}

/** One ask. Settled when won, taken, or out of re-asks; otherwise the caller waits and asks again. */
async function askOnce(mp: MultiplayerService, c: DefenceClaim, attempt: number) {
  const { resolution, reason } = await mp.claimPendingDefence!(c.sessionId, c.tabId, c.playerId, c.choice, c.extra);
  if (reason === 'won') {
    diag.info('defence', `claim of ${c.tabId} won${attempt ? ` on re-ask ${attempt}` : ''}`);
    return { settled: true, value: resolution };
  }
  if (reason === 'taken' || attempt >= CLAIM_RETRY_MS.length)
    return { settled: true, value: claimLost(c.tabId, reason) };
  diag.info('defence', `claim of ${c.tabId}: not on the server yet - re-asking in ${CLAIM_RETRY_MS[attempt]}ms`);
  return { settled: false, value: null };
}

/**
 * Claim the prompt, re-asking while the server has not got it yet.
 * Returns the resolution when won, null when lost.
 */
export async function claimWithPatience(mp: MultiplayerService, c: DefenceClaim): Promise<unknown> {
  if (!mp.claimPendingDefence) return resolveOnce(mp, c);
  for (let attempt = 0; ; attempt++) {
    const ask = await askOnce(mp, c, attempt);
    if (ask.settled) return ask.value;
    await wait(CLAIM_RETRY_MS[attempt]);
  }
}
